import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
type Size = 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  // Burgund z rozjaśnieniem w hoverze — bez zmiany nasycenia (DESIGN.md).
  primary: 'bg-primary text-on-primary hover:bg-primary/90 shadow-raised',
  secondary: 'bg-secondary text-on-secondary hover:bg-black',
  outline:
    'bg-surface text-on-surface ghost-border hover:bg-surface-container-low hover:text-primary',
  ghost: 'bg-transparent text-on-surface-variant hover:bg-surface-container-low hover:text-primary',
  danger: 'bg-error text-on-error hover:bg-error/90',
}

const SIZES: Record<Size, string> = {
  sm: 'px-4 py-2 text-xs gap-1.5',
  md: 'px-6 py-2.5 text-sm gap-2',
  lg: 'px-8 py-3.5 text-base gap-2.5',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  isLoading?: boolean
  icon?: ReactNode
  fullWidth?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  fullWidth = false,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-bold transition-all duration-150',
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading ? <Spinner /> : icon}
      {children}
    </button>
  )
}

/** Przycisk ikonowy — np. menu wiersza w tabeli. */
export function IconButton({
  label,
  icon,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-lg text-on-surface-variant',
        'transition-colors hover:bg-surface-container-low hover:text-primary',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
      {...props}
    >
      {icon}
    </button>
  )
}
